import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const TYPE_COLOR = {
  Incident: '#7c3aed',
  'Sales Order Plan': '#0891b2',
  'Project Plan': '#059669',
};
const typeColor = (t) => TYPE_COLOR[t] || '#6366f1';

function Sparkline({ values }) {
  const max = Math.max(...values, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 28 }}>
      {values.map((v, i) => {
        const pct = Math.max((v / max) * 100, v > 0 ? 8 : 0);
        return (
          <div key={i} style={{ flex: 1, height: `${pct}%`, background: v > 0 ? 'var(--accent)' : 'var(--border)', borderRadius: 1, opacity: v > 0 ? 0.8 : 0.3 }} />
        );
      })}
    </div>
  );
}

export default function RecurrenceReport() {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');

  const load = (m) => {
    setLoading(true);
    fetch(`${API}/api/reports/recurrence?months=${m}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(months); }, [months]);

  const items = (data?.items || []).filter(it => !filterType || it.issue_type === filterType);
  const types = [...new Set((data?.items || []).map(it => it.issue_type))].sort();

  return (
    <div>
      <ReportNote what="Tickets that recur for the same customer or subject." period={`Last ${months} months`} />
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)}
              className={`btn ${months === m ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '5px 14px', fontSize: 13 }}>
              {m} mois
            </button>
          ))}
        </div>
        {types.length > 1 && (
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="input" style={{ fontSize: 13, padding: '5px 10px' }}>
            <option value="">Tous les types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-2)' }}>
          {!loading && `${items.length} récurrence${items.length !== 1 ? 's' : ''} détectée${items.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {loading && <div className="loading">Analyse des récurrences…</div>}

      {!loading && data && (
        <>
          {items.length === 0 ? (
            <div className="empty"><p>Aucune récurrence détectée sur {months} mois.</p></div>
          ) : (
            <div className="card">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={th}>Client</th>
                      <th style={th}>Type</th>
                      <th style={{ ...th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...th, textAlign: 'right' }}>Mois actifs</th>
                      <th style={{ ...th, minWidth: 120 }}>
                        Tendance ({data.month_labels.map(l => l.slice(5)).join(' · ')})
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 16px', fontWeight: 600, fontSize: 14 }}>{it.project_name}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, background: typeColor(it.issue_type) + '20', color: typeColor(it.issue_type), fontWeight: 600, fontSize: 12 }}>
                            {it.issue_type}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 800, fontSize: 18, color: it.total >= 8 ? '#dc2626' : it.total >= 5 ? '#d97706' : 'var(--text)' }}>
                          {it.total}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--text-2)', fontSize: 13 }}>
                          {it.months_active}/{data.month_labels.length}
                        </td>
                        <td style={{ padding: '10px 16px', minWidth: 120 }}>
                          <Sparkline values={it.by_month} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card" style={{ marginTop: 16, padding: '14px 20px', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>
            <strong>Critères :</strong> client + type de ticket apparaissant dans ≥ 2 mois distincts avec ≥ 3 tickets résolus sur la période.
            Le total est coloré en <span style={{ color: '#d97706', fontWeight: 700 }}>orange</span> (≥5) ou <span style={{ color: '#dc2626', fontWeight: 700 }}>rouge</span> (≥8).
          </div>
        </>
      )}
    </div>
  );
}

const th = { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
